import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PlanExpiryBanner from './PlanExpiryBanner';
import BillingDueBanner from './BillingDueBanner';
import useBillingSummary from '../hooks/useBillingSummary';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useDppConfig from '../hooks/useDppConfig';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { fetchActiveModules, fetchInstituteProfile, modulesCacheKey, readCached } from '../utils/navDataCache';
import {
  LayoutDashboard, Users, DollarSign, BookOpen, Calendar, ClipboardList,
  GraduationCap, FileText, Video, BarChart2, Settings,
  ChevronLeft, ChevronRight, LogOut, Bell, Menu, X, Target, UserCircle,
  Home, BookMarked, ClipboardCheck, Award, CreditCard, Brain, Building2, Briefcase, UserPlus,
  Mail, Sparkles, Globe, File, Image, Lock, User, ChevronDown, MessageSquare, Database, Package, Shirt, Wallet, ShieldAlert, Copy, Tag, ScrollText, FlaskConical,
  Clock, Megaphone, Send, Cog, IndianRupee,
  Bus, Truck, MapPin, Wrench,
  Hotel, BedDouble, DoorOpen, Utensils, ArrowRightLeft,
  LibraryBig, CalendarCheck, AlertTriangle, Layers, Trophy, TrendingUp,
  PhoneCall, Landmark, HelpCircle, RotateCw, School, ArrowLeft, Star, Archive, FileCheck, Gift, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import { NotificationDropdown } from './Notifications';
import AIChatWidget from './AIChatWidget';
import { useReviewLink } from './reviews/ReviewInvite';
import ErrorBoundary from './ErrorBoundary';
import { hasAnyPermission, hasPermission } from '../utils/hrmsPermissions';
import { usePwaUpdate } from '../pwa/hooks/usePwaUpdate';
import { resolveInstituteArchetype, isItemVisibleForCategory, getArchetypeMeta, ARCHETYPES } from '../utils/categoryScoping';
import { resolveMediaUrl } from '../utils/mediaUtils';
import { applyTheme, initThemeFromStorage, updatePwaManifest } from '../utils/themeUtils';
import { clearAcademicScope } from '../utils/branchUtils';
import ThemeToggle from './ThemeToggle';

const OWNER_MODULE_KEYS = [
  'erp_basic', 'erp_advanced', 'attendance', 'lms', 'ai_exam', 'certificates', 'id_cards',
  'sms_whatsapp', 'email_marketing', 'fee_management', 'reports', 'marksheet_management',
  'bulk_import', 'crm_leads', 'video_sessions', 'dress_management', 'expense_management',
  'finance', 'hostel', 'transport', 'library', 'hrms', 'ai_calling_agent', 'admit_cards', 'data_backup',
];

const isItemLocked = (navItem, lockedSet) => {
  if (!lockedSet || lockedSet.size === 0) return false;
  if (Array.isArray(navItem.moduleKeys) && navItem.moduleKeys.length > 0) {
    return navItem.moduleKeys.every((key) => lockedSet.has(key));
  }
  if (navItem.moduleKey) {
    return lockedSet.has(navItem.moduleKey);
  }
  return false;
};

/** Locked module keys from cached /module-store/my-modules data. */
function lockedFromModuleData(data) {
  const active = data?.active || {};
  const locked = new Set(OWNER_MODULE_KEYS.filter((moduleKey) => !active[moduleKey]));
  (data?.roleDisabled || []).forEach((key) => locked.add(key));
  return locked;
}

export const INSTITUTE_NAV = [
  // ── Tier 1: Daily Operations & Core ERP ────────────────
  { label: 'Dashboard', icon: LayoutDashboard, path: '/institute-panel' },
  {
    label: 'Students & People', icon: Users, group: true, children: [
      { label: 'Students', icon: Users, path: '/institute-panel/students', moduleKey: 'erp_basic' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/attendance', moduleKey: 'attendance' },
      { label: 'Faculty', icon: GraduationCap, path: '/institute-panel/faculty', moduleKey: 'erp_basic' },
      { label: 'CRM / Leads', icon: Target, path: '/institute-panel/crm', moduleKey: 'crm_leads' },
      { label: 'Google Reviews', icon: Star, path: '/institute-panel/google-reviews', moduleKey: 'google_reviews' },
      { label: 'ID Cards', icon: UserCircle, path: '/institute-panel/id-cards', moduleKey: 'id_cards' },
      { label: 'Report Cards', icon: FileText, path: '/institute-panel/report-cards', moduleKey: 'marksheet_management', categories: ['school', 'college'] },
      { label: 'Certificates', icon: Award, path: '/institute-panel/certificates', moduleKey: 'certificates' },
      { label: 'Profile & Documents', icon: FileCheck, path: '/institute-panel/self-service', moduleKey: 'erp_basic' },
    ]
  },
  {
    label: 'Academics', icon: BookOpen, group: true, children: [
      { label: 'Courses', icon: BookOpen, path: '/institute-panel/courses' },
      { label: 'Batches', icon: Calendar, path: '/institute-panel/batches', categories: ['coaching'] },
      { label: 'Timetable', icon: Calendar, path: '/institute-panel/timetable' },
      { label: 'Teaching Log', icon: ClipboardList, path: '/institute-panel/teaching-log', permKey: 'teaching_log.view_all' },
      { label: 'Exams', icon: FileText, path: '/institute-panel/exams' },
      { label: 'Admit Cards', icon: Award, path: '/institute-panel/admit-cards', moduleKey: 'admit_cards' },
      { label: 'Rank & Merit List', icon: Trophy, path: '/institute-panel/merit-list', categories: ['coaching'] },
      { label: 'Daily Practice Papers', icon: ClipboardList, path: '/institute-panel/dpp', permKey: 'dpp.view' },
      { label: 'LMS', icon: BookMarked, path: '/institute-panel/lms', moduleKey: 'lms' },
      { label: 'Video Classes', icon: Video, path: '/institute-panel/video-classes', moduleKey: 'lms' },
      { label: 'Batch Performance', icon: BarChart2, path: '/institute-panel/batch-performance', categories: ['coaching'] },
      { label: 'Advanced Batch Report', icon: BarChart2, path: '/institute-panel/advanced-batch-report', categories: ['coaching'] },
      { label: 'Faculty Utilization', icon: GraduationCap, path: '/institute-panel/faculty-utilization' },
      { label: 'Marksheet Setup', icon: ClipboardList, path: '/institute-panel/marksheet-foundation', moduleKey: 'marksheet_management', categories: ['school', 'college'] },
      { label: 'Academic Setup', icon: Settings, path: '/institute-panel/academic-setup' },
    ]
  },
  {
    label: 'Finance & Accounts', icon: IndianRupee, group: true, moduleKeys: ['finance', 'fee_management', 'expense_management'], children: [
      { label: 'Fees & Invoicing', icon: CreditCard, path: '/institute-panel/fees', moduleKey: 'fee_management', permKey: 'finance.fees' },
      { label: 'Payment History', icon: ScrollText, path: '/institute-panel/fees/payment-history', moduleKey: 'fee_management', permKey: 'finance.payment_history' },
      { label: 'Defaulter Aging Report', icon: AlertTriangle, path: '/institute-panel/fees/aging-report', moduleKey: 'fee_management', permKey: 'finance.fees' },
      { label: 'Expenses & AP', icon: Wallet, path: '/institute-panel/expenses', moduleKey: 'expense_management', permKey: 'finance.fees' },
      { label: 'General Ledger & Accounting', icon: BookOpen, path: '/institute-panel/finance/accounting', moduleKey: 'finance', permKey: 'finance.fees' },
      { label: 'Treasury & Cash POS', icon: Landmark, path: '/institute-panel/finance/treasury', moduleKey: 'finance', permKey: 'finance.fees' },
      { label: 'Finance Hub & Analytics', icon: BarChart2, path: '/institute-panel/finance/dashboard', moduleKey: 'finance', permKey: 'finance.analytics' },
      { label: 'AI Cashflow Forecast', icon: Brain, path: '/institute-panel/finance/forecast', moduleKey: 'finance', permKey: 'finance.analytics' },
      { label: 'Fee Settings & Policies', icon: Settings, path: '/institute-panel/fees/settings', moduleKey: 'fee_management', permKey: ['finance.fee_settings', 'finance.settings'] },
      { label: 'Controls & Compliance', icon: ShieldAlert, path: '/institute-panel/finance/controls', moduleKey: 'finance', permKey: 'finance.audit' },
    ]
  },
  
  // â”€â”€ Tier 2: Staff & HR â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  {
    label: 'HRMS', icon: Building2, group: true, moduleKey: 'hrms', children: [
      { label: 'Employees', icon: Users, path: '/institute-panel/hrms/employees', permKey: 'hr_employees.view' },
      { label: 'Staff Attendance', icon: ClipboardCheck, path: '/institute-panel/hrms/staff-attendance' },
      { label: 'Payroll', icon: CreditCard, path: '/institute-panel/hrms/payroll', permKey: 'hr_payroll.view' },
      { label: 'Leaves & Holidays', icon: Calendar, path: '/institute-panel/hrms/leaves', permKey: 'hr_leaves.view' },
      { label: 'Departments', icon: Building2, path: '/institute-panel/hrms/departments', permKey: 'hr_departments.view' },
      { label: 'Designations', icon: Briefcase, path: '/institute-panel/hrms/designations', permKey: 'hr_designations.view' },
      { label: 'Categories', icon: Tag, path: '/institute-panel/hrms/categories', permKey: 'hr_categories.view' },
      { label: 'Shifts', icon: Calendar, path: '/institute-panel/hrms/shifts', permKey: 'hr_shifts.view' },
      { label: 'HRMS Settings', icon: Settings, path: '/institute-panel/hrms/settings', permKey: 'hr_settings.view' },
    ]
  },

  // â”€â”€ Tier 3: Communication & Engagement â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  {
    label: 'Communication', icon: Mail, group: true, moduleKeys: ['email_marketing', 'sms_whatsapp'], children: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/institute-panel/communication' },
      { label: 'Live Chat (WhatsApp)', icon: MessageSquare, path: '/institute-panel/communication/chat', moduleKey: 'sms_whatsapp' },
      { label: 'Promotions', icon: Megaphone, path: '/institute-panel/communication/promotions', moduleKey: 'email_marketing' },
      { label: 'Compose', icon: Send, path: '/institute-panel/communication/compose', moduleKey: 'email_marketing' },
      { label: 'Push Notifications', icon: Bell, path: '/institute-panel/communication/push', moduleKey: 'sms_whatsapp' },
      { label: 'Fee Reminders', icon: IndianRupee, path: '/institute-panel/communication/fee-reminders', moduleKey: 'sms_whatsapp' },
      { label: 'Attendance Alerts', icon: ClipboardCheck, path: '/institute-panel/communication/attendance-alerts', moduleKey: 'sms_whatsapp' },
      { label: 'Complaints / Support', icon: MessageSquare, path: '/institute-panel/complaints' },
      { label: 'History', icon: Clock, path: '/institute-panel/communication/history' },
      { label: 'Email Logs', icon: Mail, path: '/institute-panel/communication/email-logs' },
      { label: 'Notification Rules', icon: Settings, path: '/institute-panel/communication/notification-rules' },
      { label: 'Templates', icon: FileText, path: '/institute-panel/communication/templates', moduleKey: 'email_marketing' },
      { label: 'Settings', icon: Cog, path: '/institute-panel/communication/settings' },
    ]
  },
  {
    label: 'AI Workspace', icon: Brain, group: true, moduleKeys: ['ai_exam', 'ai_calling_agent'], children: [
      { label: 'AI Module', icon: Brain, path: '/institute-panel/ai-module', moduleKey: 'ai_exam' },
      { label: 'AI Calling Agent', icon: PhoneCall, path: '/institute-panel/ai-calling', moduleKey: 'ai_calling_agent' },
    ]
  },

  // â”€â”€ Tier 4: Campus Facilities & Infrastructure â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  {
    label: 'Transport', icon: Bus, group: true, moduleKey: 'transport', categories: ['school', 'college'], children: [
      { label: 'Dashboard', icon: Bus, path: '/institute-panel/transport', permKey: 'transport.view' },
      { label: 'Vehicles', icon: Truck, path: '/institute-panel/transport/vehicles', permKey: 'transport.vehicle' },
      { label: 'Routes & Stops', icon: MapPin, path: '/institute-panel/transport/routes', permKey: 'transport.route' },
      { label: 'Assignments', icon: UserPlus, path: '/institute-panel/transport/assignments', permKey: 'transport.student_assignment' },
      { label: 'Transport Staff', icon: UserPlus, path: '/institute-panel/transport/staff', permKey: 'transport.view' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/transport/attendance', permKey: 'transport.attendance' },
      { label: 'Maintenance', icon: Wrench, path: '/institute-panel/transport/maintenance', permKey: 'transport.maintenance' },
    ]
  },
  {
    label: 'Hostel', icon: Hotel, group: true, moduleKey: 'hostel', categories: ['college', 'school'], children: [
      { label: 'Dashboard', icon: Hotel, path: '/institute-panel/hostel' },
      { label: 'Allocations', icon: UserPlus, path: '/institute-panel/hostel/allocations' },
      { label: 'Check-In / Out', icon: ArrowRightLeft, path: '/institute-panel/hostel/check-in-out' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/hostel/attendance' },
      { label: 'Rooms & Beds', icon: BedDouble, path: '/institute-panel/hostel/rooms-beds' },
      { label: 'Fee Plans', icon: CreditCard, path: '/institute-panel/hostel/fees' },
      { label: 'Mess', icon: Utensils, path: '/institute-panel/hostel/mess' },
      { label: 'Gate Pass', icon: DoorOpen, path: '/institute-panel/hostel/gate-pass' },
      { label: 'Visitors', icon: User, path: '/institute-panel/hostel/visitors' },
      { label: 'Complaints', icon: MessageSquare, path: '/institute-panel/hostel/complaints' },
      { label: 'Maintenance', icon: Wrench, path: '/institute-panel/hostel/maintenance' },
      { label: 'Inventory', icon: Package, path: '/institute-panel/hostel/inventory' },
      { label: 'Staff', icon: Briefcase, path: '/institute-panel/hostel/staff' },
      { label: 'Reports', icon: BarChart2, path: '/institute-panel/hostel/reports' },
      { label: 'Setup (Hostels)', icon: Building2, path: '/institute-panel/hostel/setup' },
      { label: 'Buildings', icon: Building2, path: '/institute-panel/hostel/buildings' },
      { label: 'Floors', icon: Building2, path: '/institute-panel/hostel/floors' },
      { label: 'Settings', icon: Settings, path: '/institute-panel/hostel/settings' },
    ]
  },
  {
    label: 'Library', icon: LibraryBig, group: true, moduleKey: 'library', categories: ['college', 'school'], children: [
      { label: 'Dashboard', icon: LibraryBig, path: '/institute-panel/library' },
      { label: 'Issue / Return', icon: BookMarked, path: '/institute-panel/library/issue-return' },
      { label: 'Books', icon: BookOpen, path: '/institute-panel/library/books' },
      { label: 'Members', icon: Users, path: '/institute-panel/library/members' },
      { label: 'Reservations', icon: CalendarCheck, path: '/institute-panel/library/reservations' },
      { label: 'Fine Management', icon: AlertTriangle, path: '/institute-panel/library/fines' },
      { label: 'Inventory / Stock', icon: Package, path: '/institute-panel/library/inventory' },
      { label: 'Reports', icon: BarChart2, path: '/institute-panel/library/reports' },
      { label: 'Settings', icon: Settings, path: '/institute-panel/library/settings' },
    ]
  },
  { label: 'Dress Management', icon: Shirt, path: '/institute-panel/dress-management', moduleKey: 'dress_management', categories: ['school', 'college', 'coaching'] },

  // â”€â”€ Tier 5: Analytics & Growth â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  {
    label: 'Analytics & Reports', icon: TrendingUp, group: true, moduleKey: 'reports', children: [
      { label: 'Reports Hub', icon: BarChart2, path: '/institute-panel/reports', permKey: 'reports.view' },
      { label: 'Student Growth', icon: TrendingUp, path: '/institute-panel/reports/student-growth', permKey: 'reports.student_growth.view' },
      { label: 'Attendance Analytics', icon: CalendarCheck, path: '/institute-panel/reports/attendance-analytics', permKey: 'reports.attendance_analytics.view' },
      { label: 'Installment Forecast', icon: Layers, path: '/institute-panel/reports/installment-forecast', permKey: 'reports.installment_forecast.view', categories: ['coaching'] },
    ]
  },

  // â”€â”€ Tier 6: Administration & Config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  {
    label: 'Multi-Branch', icon: Building2, group: true, moduleKey: 'erp_advanced', children: [
      { label: 'Branches', icon: Building2, path: '/institute-panel/branches' },
      { label: 'Copy To Branch', icon: Copy, path: '/institute-panel/copy-to-branch' },
    ]
  },
  { label: 'Module Store', icon: Package, path: '/institute-panel/module-store' },
  { label: 'Billing & Subscription', icon: CreditCard, path: '/institute-panel/billing', requiresBillingContract: true },
  { label: 'Refer & Earn', icon: Gift, path: '/institute-panel/refer' },
  { label: 'Data Backup', icon: Archive, path: '/institute-panel/data-backup', moduleKey: 'data_backup' },
  { label: 'Settings', icon: Settings, path: '/institute-panel/settings' },
  // Pinned to the sidebar footer, just above the signed-in user / logout.
  {
    label: 'Help & Setup', icon: HelpCircle, group: true, pinBottom: true, children: [
      { label: 'School Onboarding', icon: School, path: '/institute-panel/school-onboarding', categories: ['school'], migrationItem: true },
      { label: 'Migration Hub', icon: Layers, path: '/institute-panel/migration', migrationItem: true },
      { label: 'Students Guide ↗', icon: HelpCircle, path: '/institute-panel/students/guide', target: '_blank' },
      { label: 'Academics Guide ↗', icon: HelpCircle, path: '/institute-panel/academics/guide', target: '_blank' },
      { label: 'Teaching Log Guide ↗', icon: HelpCircle, path: '/institute-panel/teaching-log/guide', target: '_blank' },
      { label: 'Finance Guide ↗', icon: HelpCircle, path: '/institute-panel/finance/guide', target: '_blank', moduleKeys: ['finance', 'fee_management', 'expense_management'] },
      { label: 'HRMS Guide ↗', icon: HelpCircle, path: '/institute-panel/hrms/guide', target: '_blank', moduleKey: 'hrms' },
      { label: 'Communication Guide ↗', icon: HelpCircle, path: '/institute-panel/communication/guide', target: '_blank', moduleKeys: ['email_marketing', 'sms_whatsapp'] },
      { label: 'Transport Guide ↗', icon: HelpCircle, path: '/institute-panel/transport/guide', target: '_blank', moduleKey: 'transport', categories: ['school', 'college'] },
      { label: 'Hostel Guide ↗', icon: HelpCircle, path: '/institute-panel/hostel/guide', target: '_blank', moduleKey: 'hostel', categories: ['college', 'school'] },
      { label: 'Library Guide ↗', icon: HelpCircle, path: '/institute-panel/library/guide', target: '_blank', moduleKey: 'library', categories: ['college', 'school'] },
      { label: 'Settings Guide ↗', icon: HelpCircle, path: '/institute-panel/settings/guide', target: '_blank' },
    ]
  },
];

const TEACHER_NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/teacher', moduleKey: 'dashboard' },
  { label: 'Daily Log', icon: ClipboardList, path: '/teacher/daily-log', moduleKey: 'teaching_log' },
  { label: 'Timetable', icon: Calendar, path: '/institute-panel/timetable', moduleKey: 'classes' },
  { label: 'Notifications', icon: Bell, path: '/teacher/notifications', moduleKey: 'notifications' },
  { label: 'My Classes', icon: BookOpen, path: '/institute-panel/video-classes', moduleKey: 'classes' },
  { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/attendance', moduleKey: 'attendance' },
  { label: 'Students', icon: Users, path: '/institute-panel/students', moduleKey: 'students' },
  { label: 'Batches', icon: Calendar, path: '/institute-panel/batches', moduleKey: 'batches' },
  { label: 'Exams', icon: FileText, path: '/institute-panel/exams', moduleKey: 'exams' },
  { label: 'Admit Cards', icon: Award, path: '/institute-panel/admit-cards', moduleKey: 'admit_cards' },
  { label: 'AI Exam Generator', icon: Sparkles, path: '/teacher/ai-exam', moduleKey: 'ai-exam' },
  { label: 'LMS Content', icon: Video, path: '/institute-panel/lms', moduleKey: 'lms' },
  { label: 'Review Us', icon: Star, path: '/review-us', requiresReviewLink: true },
];

const STAFF_NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/institute-panel' },
  {
    label: 'Students & People', icon: Users, group: true, children: [
      { label: 'Students', icon: Users, path: '/institute-panel/students', moduleKey: 'erp_basic', permKey: 'students.view' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/attendance', moduleKey: 'attendance', permKey: 'attendance.view' },
      { label: 'Faculty', icon: GraduationCap, path: '/institute-panel/faculty', moduleKey: 'erp_basic', permKey: 'faculty.view' },
      { label: 'CRM / Leads', icon: Target, path: '/institute-panel/crm', moduleKey: 'crm_leads' },
      { label: 'ID Cards', icon: UserCircle, path: '/institute-panel/id-cards', moduleKey: 'id_cards', permKey: 'id_cards.view' },
      { label: 'Report Cards', icon: FileText, path: '/institute-panel/report-cards', moduleKey: 'marksheet_management', permKey: 'marksheet.view', categories: ['school', 'college'] },
      { label: 'Certificates', icon: Award, path: '/institute-panel/certificates', moduleKey: 'certificates', permKey: 'certificates.view' },
      { label: 'Profile & Documents', icon: FileCheck, path: '/institute-panel/self-service', moduleKey: 'erp_basic', permKey: 'students.view' },
    ]
  },
  {
    label: 'Academics', icon: BookOpen, group: true, children: [
      { label: 'Courses', icon: BookOpen, path: '/institute-panel/courses', permKey: 'courses.view' },
      { label: 'Batches', icon: Calendar, path: '/institute-panel/batches', permKey: 'batches.view' },
      { label: 'Timetable', icon: Calendar, path: '/institute-panel/timetable', permKey: 'timetable.view' },
      { label: 'Exams', icon: FileText, path: '/institute-panel/exams', permKey: 'exams.view' },
      { label: 'Admit Cards', icon: Award, path: '/institute-panel/admit-cards', moduleKey: 'admit_cards', permKey: 'admit_cards.view' },
      { label: 'LMS', icon: BookMarked, path: '/institute-panel/lms', moduleKey: 'lms', permKey: 'lms.view' },
      { label: 'Video Classes', icon: Video, path: '/institute-panel/video-classes', moduleKey: 'lms', permKey: 'lms.view' },
    ]
  },
  {
    label: 'Finance & Accounts', icon: IndianRupee, group: true, moduleKeys: ['finance', 'fee_management', 'expense_management'], children: [
      { label: 'Fees & Invoicing', icon: CreditCard, path: '/institute-panel/fees', moduleKey: 'fee_management', permKey: 'finance.fees' },
      { label: 'Payment History', icon: ScrollText, path: '/institute-panel/fees/payment-history', moduleKey: 'fee_management', permKey: 'finance.payment_history' },
      { label: 'Expenses & AP', icon: Wallet, path: '/institute-panel/expenses', moduleKey: 'expense_management', permKey: 'finance.expense.view' },
      { label: 'Finance Hub', icon: BarChart2, path: '/institute-panel/finance/dashboard', moduleKey: 'finance', permKey: 'finance.analytics' },
      { label: 'Fee Settings', icon: Settings, path: '/institute-panel/fees/settings', moduleKey: 'fee_management', permKey: ['finance.fee_settings', 'finance.settings'] },
    ]
  },
  {
    label: 'HRMS', icon: Building2, group: true, moduleKey: 'hrms', children: [
      { label: 'Employees', icon: Users, path: '/institute-panel/hrms/employees', permKey: 'hr_employees.view' },
      { label: 'Staff Attendance', icon: ClipboardCheck, path: '/institute-panel/hrms/staff-attendance', permKey: 'hr_employees.view' },
      { label: 'Payroll', icon: CreditCard, path: '/institute-panel/hrms/payroll', permKey: 'hr_payroll.view' },
      { label: 'Leaves & Holidays', icon: Calendar, path: '/institute-panel/hrms/leaves', permKey: 'hr_leaves.view' },
    ]
  },
  {
    label: 'Communication', icon: Mail, group: true, moduleKeys: ['email_marketing', 'sms_whatsapp'], children: [
      { label: 'Live Chat (WhatsApp)', icon: MessageSquare, path: '/institute-panel/communication/chat', moduleKey: 'sms_whatsapp' },
      { label: 'Complaints / Support', icon: MessageSquare, path: '/institute-panel/complaints', permKey: 'complaints.view' },
    ]
  },
  {
    label: 'Transport', icon: Bus, group: true, moduleKey: 'transport', children: [
      { label: 'Dashboard', icon: Bus, path: '/institute-panel/transport', permKey: 'transport.view' },
      { label: 'Vehicles', icon: Truck, path: '/institute-panel/transport/vehicles', permKey: 'transport.vehicle' },
      { label: 'Routes & Stops', icon: MapPin, path: '/institute-panel/transport/routes', permKey: 'transport.route' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/transport/attendance', permKey: 'transport.attendance' },
    ]
  },
  {
    label: 'Hostel', icon: Hotel, group: true, moduleKey: 'hostel', children: [
      { label: 'Dashboard', icon: Hotel, path: '/institute-panel/hostel', permKey: 'hostel.view' },
      { label: 'Allocations', icon: UserPlus, path: '/institute-panel/hostel/allocations', permKey: 'hostel.allocate' },
      { label: 'Check-In / Out', icon: ArrowRightLeft, path: '/institute-panel/hostel/check-in-out', permKey: 'hostel.checkout' },
      { label: 'Attendance', icon: ClipboardCheck, path: '/institute-panel/hostel/attendance', permKey: 'hostel.attendance' },
    ]
  },
  {
    label: 'Library', icon: LibraryBig, group: true, moduleKey: 'library', children: [
      { label: 'Dashboard', icon: LibraryBig, path: '/institute-panel/library', permKey: 'library.view' },
      { label: 'Issue / Return', icon: BookMarked, path: '/institute-panel/library/issue-return', permKey: 'library.issue_return' },
      { label: 'Books', icon: BookOpen, path: '/institute-panel/library/books', permKey: 'library.books' },
      { label: 'Members', icon: Users, path: '/institute-panel/library/members', permKey: 'library.members' },
    ]
  },
  {
    label: 'AI Workspace', icon: Brain, group: true, moduleKeys: ['ai_exam', 'ai_calling_agent'], children: [
      { label: 'AI Calling Agent', icon: PhoneCall, path: '/institute-panel/ai-calling', moduleKey: 'ai_calling_agent', permKey: 'ai_calling.view' },
    ]
  },
  {
    label: 'Multi-Branch', icon: Building2, group: true, moduleKey: 'erp_advanced', children: [
      { label: 'Branches', icon: Building2, path: '/institute-panel/branches', permKey: 'branches.view' },
      { label: 'Copy To Branch', icon: Copy, path: '/institute-panel/copy-to-branch', permKey: 'branches.copy' },
    ]
  },
  { label: 'Settings', icon: Settings, path: '/institute-panel/settings', permKey: 'institute_settings.view' },
  { label: 'Review Us', icon: Star, path: '/review-us', requiresReviewLink: true },
];

const STUDENT_NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/student', moduleKey: 'dashboard' },
  { label: 'Notifications', icon: Bell, path: '/student/notifications', moduleKey: 'notifications' },
  {
    label: 'Academics', icon: BookOpen, group: true, children: [
      { label: 'My Courses', icon: BookMarked, path: '/student/courses', moduleKey: 'courses' },
      { label: 'Live Classes', icon: Video, path: '/student/live-classes', moduleKey: 'lms' },
      { label: 'Assignments', icon: ClipboardList, path: '/student/assignments', moduleKey: 'assignments' },
      { label: 'Daily Practice Papers', icon: FileText, path: '/student/dpp' },
    ]
  },
  {
    label: 'Exams & Results', icon: FileText, group: true, children: [
      { label: 'Exams', icon: FileText, path: '/student/exams', moduleKey: 'exams' },
      { label: 'Admit Cards', icon: Award, path: '/student/admit-cards' },
      { label: 'My Marksheets', icon: ScrollText, path: '/student/marksheets', categories: ['school', 'college'] },
      { label: 'Result Analysis', icon: TrendingUp, path: '/student/result-analysis', moduleKey: 'exams' },
    ]
  },
  {
    label: 'Attendance & Leaves', icon: ClipboardCheck, group: true, children: [
      { label: 'Attendance', icon: ClipboardCheck, path: '/student/attendance', moduleKey: 'attendance' },
      { label: 'Apply Leave', icon: Calendar, path: '/student/leaves', moduleKey: 'leaves' },
    ]
  },
  {
    label: 'Finance & Records', icon: CreditCard, group: true, children: [
      { label: 'My Fees', icon: CreditCard, path: '/student/fees', moduleKey: 'fees' },
      { label: 'My ID Card', icon: UserCircle, path: '/student/id-card' },
      { label: 'Certificates', icon: Award, path: '/student/certificates', moduleKey: 'certificates' },
      { label: 'My Profile & Documents', icon: FileCheck, path: '/student/my-profile' },
    ]
  },
  {
    label: 'AI Learning Tools', icon: Brain, group: true, children: [
      { label: 'AI Tutor', icon: Brain, path: '/student/ai', moduleKey: 'ai' },
      { label: 'Recommendations', icon: Sparkles, path: '/student/recommendations', moduleKey: 'ai' },
    ]
  },
  { label: 'Review Us', icon: Star, path: '/review-us', requiresReviewLink: true },
];

const ADMIN_NAV = [
  { 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/admin',
    type: 'single'
  },
  { 
    label: 'Core Management',
    icon: Building2,
    type: 'group',
    items: [
      { label: 'Institutes', icon: Home, path: '/admin/institutes' },
      { label: 'Users', icon: Users, path: '/admin/users' },
      { label: 'Categories', icon: Target, path: '/admin/categories' },
      { label: 'Cities', icon: MapPin, path: '/admin/cities' },
    ]
  },
  { 
    label: 'Content & Media',
    icon: FileText,
    type: 'group',
    items: [
      { label: 'CMS Pages', icon: File, path: '/admin/pages' },
      { label: 'Media Library', icon: Image, path: '/admin/media' },
      { label: 'SEO Blogs', icon: Globe, path: '/admin/seo-blogs' },
      { label: 'Landing Page CMS', icon: Sparkles, path: '/admin/landing-cms' },
    ]
  },
  { 
    label: 'Business & Sales',
    icon: CreditCard,
    type: 'group',
    items: [
      { label: 'Subscriptions', icon: CreditCard, path: '/admin/subscriptions' },
      { label: 'Billing', icon: IndianRupee, path: '/admin/billing' },
      { label: 'Website Leads', icon: MessageSquare, path: '/admin/website-leads' },
      { label: 'Institute Sales Leads', icon: Building2, path: '/admin/institute-sales-leads' },
      { label: 'Communication', icon: Mail, path: '/admin/communication' },
      { label: 'Analytics', icon: BarChart2, path: '/admin/analytics' },
    ]
  },
  { 
    label: 'System & Tools',
    icon: Settings,
    type: 'group',
    items: [
      { label: 'Institute Directory', icon: Database, path: '/admin/seed-directory' },
      { label: 'Demo Data Manager', icon: FlaskConical, path: '/admin/demo-data' },
      { label: 'Database Backup', icon: Database, path: '/admin/database-backup' },
      { label: 'Multi-Institute', icon: Building2, path: '/admin/multi-institute' },
      { label: 'Storage Settings', icon: Settings, path: '/admin/storage' },
      { label: 'Google Maps', icon: MapPin, path: '/admin/google-maps' },
      { label: 'Country Settings', icon: Globe, path: '/admin/countries' },
      { label: 'Growth', icon: Rocket, path: '/admin/growth' },
      { label: 'AI Settings', icon: Brain, path: '/admin/ai-settings' },
      { label: 'AI Audit', icon: ShieldAlert, path: '/admin/audit' },
      { label: 'Payment Settings', icon: CreditCard, path: '/admin/payment-settings' },
      { label: 'Login Security', icon: ShieldAlert, path: '/admin/login-security' },
    ]
  },
];

const PARENT_NAV = [
  { label: 'Overview', icon: LayoutDashboard, path: '/parent', moduleKey: 'dashboard' },
  { label: 'Notifications', icon: Bell, path: '/parent/notifications', moduleKey: 'notifications' },
  {
    label: 'Academics & Reports', icon: BookOpen, group: true, children: [
      { label: 'Exam Results', icon: FileText, path: '/parent/exams', moduleKey: 'exams' },
      { label: 'Report Cards', icon: ScrollText, path: '/parent/marksheets', moduleKey: 'marksheet_management', categories: ['school', 'college'] },
      { label: 'Admit Cards', icon: Award, path: '/student/admit-cards', moduleKey: 'admit_cards' },
      { label: 'AI Analysis', icon: Brain, path: '/parent/ai-analysis', moduleKey: 'ai-analysis' },
    ]
  },
  {
    label: 'Attendance & Leaves', icon: ClipboardCheck, group: true, children: [
      { label: 'Attendance', icon: ClipboardCheck, path: '/parent/attendance', moduleKey: 'attendance' },
      { label: 'Leave Requests', icon: Calendar, path: '/parent/leaves', moduleKey: 'leaves' },
    ]
  },
  {
    label: 'Finance & Records', icon: CreditCard, group: true, children: [
      { label: 'Fee History', icon: CreditCard, path: '/parent/fees', moduleKey: 'fees' },
      { label: 'Profile & Documents', icon: FileCheck, path: '/parent/my-profile' },
    ]
  },
  { label: 'Review Us', icon: Star, path: '/review-us', requiresReviewLink: true },
];

const HRMS_NAV_PERMISSION_MAP = {
  '/institute-panel/hrms/employees': 'hr_employees.view',
  '/institute-panel/hrms/categories': 'hr_categories.view',
  '/institute-panel/hrms/departments': 'hr_departments.view',
  '/institute-panel/hrms/designations': 'hr_designations.view',
  '/institute-panel/hrms/shifts': 'hr_shifts.view',
  '/institute-panel/hrms/leaves': 'hr_leaves.view',
  '/institute-panel/hrms/payroll': 'hr_payroll.view',
  '/institute-panel/hrms/settings': 'hr_settings.view',
};

const NAV_PERMISSION_MAP = {
  ...HRMS_NAV_PERMISSION_MAP,
  '/institute-panel/transport': 'transport.view',
  '/institute-panel/transport/vehicles': 'transport.vehicle',
  '/institute-panel/transport/staff': 'transport.view',
  '/institute-panel/transport/routes': 'transport.route',
  '/institute-panel/transport/assignments': 'transport.student_assignment',
  '/institute-panel/transport/maintenance': 'transport.maintenance',
  '/institute-panel/transport/attendance': 'transport.attendance',
  '/institute-panel/marksheet-foundation': 'marksheet.settings',
  '/institute-panel/fees': 'finance.fees',
  '/institute-panel/fees/payment-history': 'finance.payment_history',
  '/institute-panel/fees/settings': ['finance.fee_settings', 'finance.settings'],
  '/institute-panel/finance/dashboard': 'finance.analytics',
  '/institute-panel/expenses': 'finance.expense.view',
  '/institute-panel/analytics': 'finance.analytics',
  '/institute-panel/ai-calling': 'ai_calling.view',
  '/institute-panel/complaints': 'complaints.view',
  '/institute-panel/library': 'library.view',
  '/institute-panel/library/books': 'library.books',
  '/institute-panel/library/members': 'library.members',
  '/institute-panel/library/issue-return': 'library.issue_return',
  '/institute-panel/library/reservations': 'library.reservation',
  '/institute-panel/library/fines': 'library.fine',
  '/institute-panel/library/inventory': 'library.inventory',
  '/institute-panel/library/reports': 'library.reports',
  '/institute-panel/library/settings': 'library.settings',
  '/institute-panel/hostel': 'hostel.view',
  '/institute-panel/hostel/setup': 'hostel.settings',
  '/institute-panel/hostel/buildings': 'hostel.settings',
  '/institute-panel/hostel/floors': 'hostel.settings',
  '/institute-panel/hostel/rooms-beds': 'hostel.settings',
  '/institute-panel/hostel/allocations': 'hostel.allocate',
  '/institute-panel/hostel/check-in-out': 'hostel.checkout',
  '/institute-panel/hostel/gate-pass': 'hostel.leave',
  '/institute-panel/hostel/visitors': 'hostel.visitor',
  '/institute-panel/hostel/attendance': 'hostel.attendance',
  '/institute-panel/hostel/mess': 'hostel.view',
  '/institute-panel/hostel/complaints': 'hostel.complaint',
  '/institute-panel/hostel/maintenance': 'hostel.maintenance',
  '/institute-panel/hostel/inventory': 'hostel.inventory',
  '/institute-panel/hostel/fees': 'hostel.fees',
  '/institute-panel/hostel/staff': 'hostel.settings',
  '/institute-panel/hostel/reports': 'hostel.reports',
  '/institute-panel/hostel/settings': 'hostel.settings',
};

const ROLE_NAV = {
  super_admin: ADMIN_NAV,
  institute_admin: INSTITUTE_NAV,
  teacher: TEACHER_NAV,
  staff: STAFF_NAV,
  admin: STAFF_NAV,
  counselor: STAFF_NAV,
  receptionist: STAFF_NAV,
  accountant: STAFF_NAV,
  hr: STAFF_NAV,
  academic_coordinator: STAFF_NAV,
  exam_incharge: STAFF_NAV,
  librarian: STAFF_NAV,
  hostel_warden: STAFF_NAV,
  transport_incharge: STAFF_NAV,
  student: STUDENT_NAV,
  parent: PARENT_NAV,
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  institute_admin: 'Institute',
  teacher: 'Teacher',
  staff: 'Staff',
  student: 'Student',
  parent: 'Parent',
};

const ROLE_COLORS = {
  super_admin: 'bg-purple-600',
  institute_admin: 'bg-indigo-600',
  teacher: 'bg-emerald-600',
  staff: 'bg-cyan-600',
  student: 'bg-blue-600',
  parent: 'bg-amber-600',
};

const isSidebarPathActive = (currentPath, targetPath) => {
  if (!targetPath) return false;
  const exactOnlyRoots = ['/institute-panel', '/admin', '/teacher', '/student', '/parent'];
  if (exactOnlyRoots.includes(targetPath)) {
    return currentPath === targetPath;
  }
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

// Bottom nav items per role (max 5 items for mobile bottom bar)
const REVIEW_ROLES = new Set(['student', 'parent', 'teacher', 'staff', 'admin', 'counselor', 'receptionist', 'accountant', 'hr', 'academic_coordinator']);

const BOTTOM_NAV = {
  super_admin: [
    { label: 'Home', icon: LayoutDashboard, path: '/admin' },
    { label: 'Institutes', icon: Home, path: '/admin/institutes' },
    { label: 'Users', icon: Users, path: '/admin/users' },
    { label: 'Analytics', icon: BarChart2, path: '/admin/analytics' },
  ],
  institute_admin: [
    { label: 'Home', icon: LayoutDashboard, path: '/institute-panel' },
    { label: 'Students', icon: Users, path: '/institute-panel/students' },
    { label: 'Fees', icon: CreditCard, path: '/institute-panel/fees' },
    { label: 'Exams', icon: FileText, path: '/institute-panel/exams' },
  ],
  teacher: [
    { label: 'Home', icon: LayoutDashboard, path: '/teacher' },
    // Logged after every class, so it gets a bottom-bar slot (Teaching Log plan §4.7).
    { label: 'Daily Log', icon: ClipboardList, path: '/teacher/daily-log', moduleKey: 'teaching_log' },
    { label: 'Classes', icon: BookOpen, path: '/institute-panel/video-classes' },
    { label: 'Exams', icon: FileText, path: '/institute-panel/exams' },
  ],
  student: [
    { label: 'Home', icon: LayoutDashboard, path: '/student' },
    { label: 'Courses', icon: BookMarked, path: '/student/courses' },
    { label: 'Attendance', icon: ClipboardCheck, path: '/student/attendance' },
    { label: 'Exams', icon: FileText, path: '/student/exams' },
    { label: 'Fees', icon: CreditCard, path: '/student/fees' },
  ],
  parent: [
    { label: 'Home', icon: LayoutDashboard, path: '/parent' },
    { label: 'Fees', icon: CreditCard, path: '/parent/fees' },
    { label: 'Attendance', icon: ClipboardCheck, path: '/parent/attendance' },
    { label: 'Leaves', icon: Calendar, path: '/parent/leaves' },
  ],
  staff: [
    { label: 'Home', icon: LayoutDashboard, path: '/institute-panel' },
    { label: 'CRM', icon: Target, path: '/institute-panel/crm' },
    { label: 'Chat', icon: MessageSquare, path: '/institute-panel/communication/chat' },
    { label: 'Students', icon: Users, path: '/institute-panel/students' },
  ],
};

export default function DashboardLayout({ children, title }) {
  const navigate = useNavigate();
  const { user, logout, updateUser, isImpersonating, stopImpersonation } = useAuth();
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const canExitImpersonation = isImpersonating || (typeof window !== 'undefined' && Boolean(sessionStorage.getItem('original_superadmin_token')));

  const handleExitImpersonation = async () => {
    try {
      setExitingImpersonation(true);
      if (stopImpersonation) {
        await stopImpersonation();
      } else {
        const origToken = sessionStorage.getItem('original_superadmin_token');
        const origUser = sessionStorage.getItem('original_superadmin_user');
        sessionStorage.removeItem('original_superadmin_token');
        sessionStorage.removeItem('original_superadmin_user');
        clearAcademicScope();
        if (origToken) localStorage.setItem('token', origToken);
        if (origUser) localStorage.setItem('user', origUser);
      }
      toast.success('Returned to Super Admin portal');
      navigate('/admin/institutes');
    } catch (err) {
      console.error(err);
      toast.error('Failed to exit impersonation');
    } finally {
      setExitingImpersonation(false);
    }
  };

  const practice = useDppConfig();
  const isStudentSidebar = user?.role === 'student';
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [lockedModules, setLockedModules] = useState(() => {
    const cached = readCached(modulesCacheKey(user))?.data;
    if (cached) return lockedFromModuleData(cached);
    return user?.role === 'institute_admin' ? new Set(OWNER_MODULE_KEYS) : new Set();
  });
  const [openGroups, setOpenGroups] = useState({});
  const [userInstitutes, setUserInstitutes] = useState([]);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showBranchSwitcher, setShowBranchSwitcher] = useState(false);
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
  const [activeSession, setActiveSession] = useState(null);
  const [headerSessions, setHeaderSessions] = useState([]);
  const mainRef = useRef(null);
  const { updating: isHardRefreshing, updateNow: handleHardRefresh } = usePwaUpdate();

  // Institute Branding state (white-label logo, name, banner, theme)
  const [branding, setBranding] = useState(() => {
    const cached = localStorage.getItem('institute_branding');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.name || parsed?.logo || parsed?.primary_color) return parsed;
      } catch {}
    }
    return {
      name: user?.institute_name || '',
      logo: user?.institute_logo || '',
      favicon: user?.institute_favicon || user?.institute_logo || '',
      primary_color: user?.institute_primary_color || '',
      secondary_color: user?.institute_secondary_color || '',
      banner: user?.institute_banner || '',
      tagline: user?.institute_tagline || '',
    };
  });

  // Apply cached institute branding CSS variables immediately on dashboard mount
  useEffect(() => {
    initThemeFromStorage();
  }, []);

  useEffect(() => {
    const syncBranding = (data) => {
      if (!data) return;
      const updated = {
        name: data.name || '',
        logo: data.logo || '',
        favicon: data.favicon || data.favicon_url || data.logo || '',
        primary_color: data.primary_color || data.institute_primary_color || '',
        secondary_color: data.secondary_color || data.institute_secondary_color || '',
        banner: data.banner_image_url || data.banner || '',
        tagline: data.tagline || '',
        city: data.city || '',
        state: data.state || '',
      };
      setBranding(updated);
      localStorage.setItem('institute_branding', JSON.stringify(updated));
      if (updated.primary_color || updated.secondary_color) {
        applyTheme(updated);
      }
    };

    const handleBrandingEvent = (e) => {
      if (e.detail) syncBranding(e.detail);
      else {
        const cached = localStorage.getItem('institute_branding');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            setBranding(parsed);
            if (parsed.primary_color || parsed.secondary_color) {
              applyTheme(parsed);
            }
          } catch {}
        }
      }
    };

    window.addEventListener('institute-branding-updated', handleBrandingEvent);
    window.addEventListener('storage', handleBrandingEvent);

    if (localStorage.getItem('token')) {
      fetchInstituteProfile(user).then(data => {
        if (data) syncBranding(data);
      }).catch(() => {});
    }

    return () => {
      window.removeEventListener('institute-branding-updated', handleBrandingEvent);
      window.removeEventListener('storage', handleBrandingEvent);
    };
  }, [user?.institute_id]);

  useEffect(() => {
    if (user?.institute_name || user?.institute_logo || user?.institute_primary_color || user?.institute_secondary_color) {
      setBranding((prev) => {
        const next = {
          ...prev,
          name: user.institute_name || prev.name || '',
          logo: user.institute_logo || prev.logo || '',
          favicon: user.institute_favicon || prev.favicon || user.institute_logo || prev.logo || '',
          primary_color: user.institute_primary_color || prev.primary_color || '',
          secondary_color: user.institute_secondary_color || prev.secondary_color || '',
          banner: user.institute_banner || prev.banner || '',
          tagline: user.institute_tagline || prev.tagline || '',
          city: user.institute_city || prev.city || '',
          state: user.institute_state || prev.state || '',
        };
        if (next.primary_color || next.secondary_color) {
          applyTheme(next);
        }
        return next;
      });
    }
  }, [user?.institute_name, user?.institute_logo, user?.institute_favicon, user?.institute_primary_color, user?.institute_secondary_color, user?.institute_banner, user?.institute_tagline, user?.institute_city, user?.institute_state]);

  useEffect(() => {
    const instName = branding.name || user?.institute_name || 'ERP Portal';
    document.title = title ? `${title} | ${instName}` : instName;

    // Dynamically update browser tab favicon and PWA manifest for desktop/laptop and mobile
    const favIcon = branding.favicon || branding.logo;
    if (favIcon) {
      const resolved = resolveMediaUrl(favIcon);
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = resolved;
    }

    // Update PWA install manifest, apple-touch-icon, and status bar color
    updatePwaManifest({
      ...branding,
      institute_id: user?.institute_id || branding.institute_id,
    });
  }, [title, branding.name, branding.favicon, branding.logo, branding.primary_color, user?.institute_name, user?.institute_id]);

  // Scroll main content to top on every route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname]);

  // Resolve the institute's currently active academic session for the header pill.
  // Only matters for institute admin / teacher / student / parent â€” super_admin sees nothing.
  useEffect(() => {
    const role = user?.role;
    if (!role || !['institute_admin', 'teacher', 'student', 'parent'].includes(role)) return;
    let cancelled = false;

    const fetchSession = async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const token = localStorage.getItem('token');
        if (!token) return;
        const branchId = localStorage.getItem('selected_branch_id') || '';
        const params = branchId ? { branch_id: branchId } : {};
        const [activeRes, allRes] = await Promise.all([
          axios.get(`${API}/academic-foundation/sessions/active`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
          axios.get(`${API}/academics/sessions`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
        ]);
        if (!cancelled) {
          setActiveSession(activeRes?.data || null);
          if (activeRes?.data?.id) {
            localStorage.setItem('active_academic_session_id', activeRes.data.id);
            localStorage.setItem('active_academic_session_name', activeRes.data.name || '');
          }
          if (allRes?.data && Array.isArray(allRes.data)) {
            setHeaderSessions(allRes.data);
          }
        }
      } catch (e) {
        if (!cancelled) setActiveSession(null);
      }
    };

    fetchSession();

    window.addEventListener('academic-session-updated', fetchSession);
    return () => {
      cancelled = true;
      window.removeEventListener('academic-session-updated', fetchSession);
    };
  }, [user?.role, selectedBranch?.id]);

  const fetchGlobalUnreadCount = useCallback(async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/notifications`, { params: { limit: 1 }, headers: { Authorization: `Bearer ${token}` } });
      setGlobalUnreadCount(res.data.unread_count || 0);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchGlobalUnreadCount();
    // Poll every 30s only while the tab is visible; refresh as soon as it is shown again.
    const interval = setInterval(() => {
      if (!document.hidden) fetchGlobalUnreadCount();
    }, 30000);
    const onVisible = () => { if (!document.hidden) fetchGlobalUnreadCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, fetchGlobalUnreadCount]);

  const [instituteCategory, setInstituteCategory] = useState(
    () => user?.institute_category || localStorage.getItem('institute_category') || ''
  );

  useEffect(() => {
    const handleCategoryChanged = () => {
      const updatedCat = localStorage.getItem('institute_category') || '';
      setInstituteCategory(updatedCat);
    };
    window.addEventListener('institute-category-changed', handleCategoryChanged);
    window.addEventListener('storage', handleCategoryChanged);
    return () => {
      window.removeEventListener('institute-category-changed', handleCategoryChanged);
      window.removeEventListener('storage', handleCategoryChanged);
    };
  }, []);

  useEffect(() => {
    if (user?.institute_category) {
      setInstituteCategory(user.institute_category);
      localStorage.setItem('institute_category', user.institute_category);
      return;
    }
    if (user?.role === 'institute_admin') {
      if (localStorage.getItem('token')) {
        fetchInstituteProfile(user)
          .then(data => {
            const cat = data?.category || '';
            if (cat) {
              setInstituteCategory(cat);
              localStorage.setItem('institute_category', cat);
            }
          })
          .catch(() => {});
      }
    }
  }, [user?.role, user?.institute_category]);

  const currentArchetype = useMemo(() => {
    return resolveInstituteArchetype(user?.institute_category || instituteCategory, user?.institute_subcategory);
  }, [user?.institute_category, user?.institute_subcategory, instituteCategory]);

  const archetypeMeta = useMemo(() => {
    return getArchetypeMeta(currentArchetype);
  }, [currentArchetype]);

  const navItems = useMemo(() => ROLE_NAV[user?.role] || [], [user?.role]);
  const billingSummary = useBillingSummary(user);
  const hasBillingContract = Boolean(billingSummary?.contract?.id);
  const billingDue = hasBillingContract && Number(billingSummary?.account_summary?.total_payable || 0) > 0;
  const bottomNavItems = (BOTTOM_NAV[user?.role] || []).filter((item) => !isItemLocked(item, lockedModules));
  // 'Review Us' shows only once the institute has a review link set up.
  const reviewLink = useReviewLink(REVIEW_ROLES.has(user?.role) ? user : null);
  const roleLabel = ROLE_LABELS[user?.role] || 'User';
  const roleColor = ROLE_COLORS[user?.role] || 'bg-slate-600';
  const visibleNavItems = useMemo(() => navItems.map((item) => {
    if (item.requiresBillingContract && !hasBillingContract) return null;
    if (item.requiresBillingContract && billingDue) item = { ...item, badge: 'Due', badgeTone: 'alert' };
    if (!isItemVisibleForCategory(item, currentArchetype)) return null;
    if (user?.role !== 'super_admin' && isItemLocked(item, lockedModules)) return null;
    if (item.requiresReviewLink && !reviewLink?.available) return null;

    if (item.migrationItem) {
      const isOwner = user?.role === 'institute_admin' && (user?.is_institute_owner || user?.id === user?.institute_owner_id || (!user?.is_sub_admin && !user?.parent_user_id));
      if (!isOwner) return null;
      if (user?.migration_hub_enabled === false || user?.hide_migration_hub === true) return null;
    }

    if (!item.children) {
      // Check NAV_PERMISSION_MAP first, then item's own permKey
      const permissionKeys = NAV_PERMISSION_MAP[item.path] || item.permKey;
      return permissionKeys
        ? (Array.isArray(permissionKeys) ? hasAnyPermission(user, permissionKeys) : hasPermission(user, permissionKeys))
          ? item
          : null
        : item;
    }

    const children = item.children.filter((child) => {
      if (child.migrationItem) {
        const isOwner = user?.role === 'institute_admin' && (user?.is_institute_owner || user?.id === user?.institute_owner_id || (!user?.is_sub_admin && !user?.parent_user_id));
        if (!isOwner) return false;
        if (user?.migration_hub_enabled === false || user?.hide_migration_hub === true) return false;
      }
      if (!isItemVisibleForCategory(child, currentArchetype, { dpp_enabled: !practice.loading && !practice.error && practice.enabled })) return false;
      if (user?.role !== 'super_admin' && isItemLocked(child, lockedModules)) return false;
      // Check NAV_PERMISSION_MAP first, then child's own permKey
      const permissionKeys = NAV_PERMISSION_MAP[child.path] || child.permKey;
      if (!permissionKeys) return true;
      return Array.isArray(permissionKeys)
        ? hasAnyPermission(user, permissionKeys)
        : hasPermission(user, permissionKeys);
    });
    return { ...item, children: children.map(child => ['/institute-panel/dpp', '/student/dpp'].includes(child.path) ? { ...child, label: practice.title } : child) };
  }).filter((item) => item && (!item.children || item.children.length > 0)), [navItems, user, currentArchetype, practice.enabled, practice.title, practice.loading, practice.error, lockedModules, reviewLink, hasBillingContract, billingDue]);

  const mainNavItems = useMemo(() => visibleNavItems.filter((item) => !item.pinBottom), [visibleNavItems]);
  const pinnedNavItems = useMemo(() => visibleNavItems.filter((item) => item.pinBottom), [visibleNavItems]);

  // Auto-open group if a child route is active (only one active group at a time)
  useEffect(() => {
    let activeGroupLabel = null;
    visibleNavItems.forEach(item => {
      // New group type
      if (item.type === 'group' && item.items) {
        const isChildActive = item.items.some(child => isSidebarPathActive(location.pathname, child.path));
        if (isChildActive) activeGroupLabel = item.label;
      }
      // Old group type (backward compatibility)
      if (item.group && item.children) {
        const isChildActive = item.children.some(child => isSidebarPathActive(location.pathname, child.path));
        if (isChildActive) activeGroupLabel = item.label;
      }
    });
    if (activeGroupLabel) {
      setOpenGroups({ [activeGroupLabel]: true });
    } else {
      setOpenGroups({});
    }
  }, [location.pathname, visibleNavItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (label) => {
    setOpenGroups(prev => {
      const wasOpen = prev[label];
      return { [label]: !wasOpen };
    });
  };

  // Fetch module permissions
  useEffect(() => {
    if (!user?.role || user?.role === 'super_admin') return undefined;

    let active = true;
    // Shared across page navigations: a fresh result (<60s) is reused, plan changes force a refetch.
    const refreshModules = (force = false) => fetchActiveModules(user, { force })
      .then((data) => {
        if (!active) return;
        const next = lockedFromModuleData(data);
        setLockedModules((previous) => (previous.size === next.size && [...next].every((key) => previous.has(key)) ? previous : next));
      })
      .catch(() => {});
    const onFocus = () => refreshModules(false);
    const onPlanChange = () => refreshModules(true);

    refreshModules(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('institute-plan-updated', onPlanChange);
    window.addEventListener('storage', onPlanChange);
    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('institute-plan-updated', onPlanChange);
      window.removeEventListener('storage', onPlanChange);
    };
  }, [user?.role, user?.institute_id]);

  // Fetch user's institutes for switcher
  useEffect(() => {
    if (['student', 'teacher'].includes(user?.role)) {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('token');
      axios.get(`${API}/multi-institute/user-institutes`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUserInstitutes(res.data.institutes || []))
        .catch(() => {});
    }
  }, [user?.role, user?.institute_id]);

  // Fetch branches for institute_admin
  useEffect(() => {
    if (user?.role === 'institute_admin') {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      axios.get(`${API}/branches`)
        .then(res => {
          const branchList = Array.isArray(res.data) ? res.data : (res.data.branches || []);
          const activeBranches = branchList.filter(b => b.is_active !== false);
          setBranches(activeBranches);
          
          // Load saved branch from localStorage
          const savedBranchId = localStorage.getItem('selected_branch_id');
          if (savedBranchId) {
            const savedBranch = activeBranches.find(b => b.id === savedBranchId);
            if (savedBranch) {
              setSelectedBranch(savedBranch);
            } else if (activeBranches.length > 0) {
              // Saved branch not found or inactive, default to first active branch
              const firstBranch = activeBranches[0];
              setSelectedBranch(firstBranch);
              localStorage.setItem('selected_branch_id', firstBranch.id);
              window.dispatchEvent(new Event('selected-branch-changed'));
            }
          } else if (activeBranches.length > 0) {
            // No saved branch, default to first active branch
            const firstBranch = activeBranches[0];
            setSelectedBranch(firstBranch);
            localStorage.setItem('selected_branch_id', firstBranch.id);
            window.dispatchEvent(new Event('selected-branch-changed'));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch branches:', err);
          setBranches([]);
        });
    }
  }, [user?.role]);

  const handleSwitchBranch = (branch) => {
    if (!window.dispatchEvent(new Event("before-academic-scope-change", { cancelable: true }))) return;
    setSelectedBranch(branch);
    localStorage.setItem('selected_branch_id', branch?.id || '');
    window.dispatchEvent(new Event('selected-branch-changed'));
    setShowBranchSwitcher(false);
    // Reload to apply branch filter
    window.location.reload();
  };

  const handleSwitchInstitute = async (instituteId) => {
    if (instituteId === user?.institute_id) return;
    setSwitching(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/multi-institute/switch`, { institute_id: instituteId }, { headers: { Authorization: `Bearer ${token}` } });
      clearAcademicScope();
      localStorage.setItem('token', res.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      updateUser(res.data.user);
      setShowSwitcher(false);
      window.location.reload();
    } catch (e) {
      console.error('Switch failed:', e);
    } finally {
      setSwitching(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const renderFlatNavItem = (item) => {
    const Icon = item.icon;
    const isActive = isSidebarPathActive(location.pathname, item.path) &&
      !visibleNavItems.some(other => other.path && other.path !== item.path && location.pathname === other.path);
    const moduleKey = item.moduleKey;
    const isLocked = moduleKey && lockedModules.has(moduleKey);
    return isLocked ? null : (
      <Link
        key={item.path}
        to={item.path}
        target={item.target || undefined}
        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
        onClick={() => setMobileOpen(false)}
        className={`sidebar-item ${isStudentSidebar ? '!gap-2.5 !py-1.5' : ''} ${isActive ? 'active' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
      >
        <span className="relative flex-shrink-0">
          <Icon className="w-4 h-4" />
          {collapsed && item.badgeTone === 'alert' && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />}
        </span>
        {!collapsed && (
          <div className="flex items-center justify-between flex-1 min-w-0">
            <span className="truncate">{item.label}</span>
            {item.badge && (
              <span className={`ml-auto text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-md border ${item.badgeTone === 'alert' ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-300/60 dark:border-rose-800 animate-pulse' : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-800'}`}>
                {item.badge}
              </span>
            )}
          </div>
        )}
      </Link>
    );
  };

  const renderLegacyGroup = (item) => {
    const groupModuleKey = item.moduleKey;
    if (groupModuleKey && lockedModules.has(groupModuleKey)) return null;
    const GroupIcon = item.icon;
    const isGroupOpen = openGroups[item.label] || false;
    const isChildActive = item.children.some(child => isSidebarPathActive(location.pathname, child.path));

    return (
      <div key={item.label}>
        <button
          onClick={() => toggleGroup(item.label)}
          data-testid={`nav-group-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
          className={`sidebar-item w-full justify-between ${isChildActive ? 'font-semibold brand-primary-light-bg text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'}`}
        >
          <div className="flex items-center gap-3">
            <GroupIcon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </div>
          {!collapsed && (
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isGroupOpen ? 'rotate-180' : ''}`} />
          )}
        </button>
        {!collapsed && isGroupOpen && (
          <div className="ml-4 pl-3 border-l border-slate-200 dark:border-slate-800 space-y-0.5 py-0.5">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const isActive = isSidebarPathActive(location.pathname, child.path) &&
                !item.children.some(other => other.path !== child.path && location.pathname === other.path);
              const moduleKey = child.moduleKey;
              const isLocked = moduleKey && lockedModules.has(moduleKey);
               return isLocked ? null : (
                 <Link key={child.path} to={child.path}
                   target={child.target || undefined}
                   rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
                   data-testid={`nav-${child.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                   onClick={() => setMobileOpen(false)}
                   className={`sidebar-item text-[13px] py-1.5 ${isActive ? 'active' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}>
                   <ChildIcon className="w-3.5 h-3.5 flex-shrink-0" />
                   <span>{child.label}</span>
                 </Link>
               );
            })}
          </div>
        )}
      </div>
    );
  };

  // Pages that fill the screen (WhatsApp-style chat) get slimmer page padding
  const isFullHeightPage = location.pathname === '/institute-panel/communication/chat';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo & Institute Branding */}
      <div className={`flex items-center gap-3 px-4 border-b border-slate-100 dark:border-slate-800 ${isStudentSidebar ? 'py-3' : 'py-4'} ${collapsed ? 'justify-center' : ''}`}>
        {branding.logo ? (
          <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700 shadow-2xs flex items-center justify-center overflow-hidden flex-shrink-0 p-0.5">
            <img
              src={resolveMediaUrl(branding.logo)}
              alt={branding.name || 'Logo'}
              className="w-full h-full object-contain rounded-lg"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        ) : (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-2xs font-outfit text-white font-bold text-sm"
            style={{ backgroundColor: 'var(--brand-primary, #4f46e5)' }}
          >
            {branding.name ? branding.name.charAt(0).toUpperCase() : <GraduationCap className="w-4 h-4 text-white" />}
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <span
              className="font-outfit font-bold text-slate-900 dark:text-white text-sm sm:text-base truncate block leading-tight"
              title={branding.name || user?.institute_name || 'My Institute'}
            >
              {branding.name || user?.institute_name || 'My Institute'}
            </span>
          </div>
        )}
      </div>

      {/* Nav Items */}
      <nav className={`flex-1 overflow-y-auto px-3 space-y-0.5 ${isStudentSidebar ? 'sidebar-scroll py-2' : 'py-4'}`}>
        {mainNavItems.map((item) => {
          // Handle new group type
          if (item.type === 'group' && item.items) {
            const GroupIcon = item.icon;
            const isGroupOpen = openGroups[item.label] || false;
            const isChildActive = item.items.some(child => isSidebarPathActive(location.pathname, child.path));

            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  data-testid={`nav-group-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className={`sidebar-item w-full justify-between ${isChildActive ? 'font-semibold brand-primary-light-bg text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'}`}
                >
                  <div className="flex items-center gap-3">
                    <GroupIcon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </div>
                  {!collapsed && (
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isGroupOpen ? 'rotate-180' : ''}`} />
                  )}
                </button>
                {!collapsed && isGroupOpen && (
                  <div className="ml-4 pl-3 border-l border-slate-200 dark:border-slate-800 space-y-0.5 py-0.5">
                    {item.items.map((child) => {
                      const ChildIcon = child.icon;
                      const isActive = location.pathname === child.path;
                      const moduleKey = child.moduleKey;
                      const isLocked = moduleKey && lockedModules.has(moduleKey);
                      return isLocked ? null : (
                        <Link key={child.path} to={child.path}
                          data-testid={`nav-${child.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                          onClick={() => setMobileOpen(false)}
                          className={`sidebar-item text-[13px] py-1.5 ${isActive ? 'active' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}>
                          <ChildIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Old grouped dropdown (for backward compatibility)
          if (item.group && item.children) return renderLegacyGroup(item);

          return renderFlatNavItem(item);
        })}
      </nav>

      {/* Pinned items (e.g. Help & Setup) sit at the bottom; logout lives in the header profile menu */}
      {pinnedNavItems.length > 0 && (
        <div className={`border-t border-slate-100 dark:border-slate-800 px-3 ${isStudentSidebar ? 'py-1.5' : 'py-2'} space-y-0.5 max-h-[45vh] overflow-y-auto`}>
          {pinnedNavItems.map((item) => (item.group && item.children ? renderLegacyGroup(item) : renderFlatNavItem(item)))}
        </div>
      )}
    </div>
  );

  return (
    // Desktop: the shell is exactly the screen tall; the sidebar and the page scroll inside it.
    // With only min-h-screen a long sidebar (an open menu) stretched the whole page, leaving
    // full-height pages such as Live Chat with empty space under them.
    <div className="app-shell flex flex-col min-h-screen md:h-[100dvh] md:min-h-0 bg-slate-50 dark:bg-slate-950 overflow-x-hidden">
      {/* Super Admin Impersonation Notice Banner */}
      {canExitImpersonation && (
        <aside
          role="alert"
          aria-label="Super Admin Impersonation Banner"
          className="w-full bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white px-4 py-2 sm:py-2.5 shadow-md flex items-center justify-between gap-3 text-xs sm:text-sm font-medium z-50 sticky top-0 border-b border-amber-500/30 shrink-0"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-2.5 w-2.5 rounded-full bg-amber-200 animate-pulse shrink-0" />
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <span className="font-bold uppercase tracking-wider text-[10px] sm:text-[11px] bg-black/25 px-2 py-0.5 rounded-md text-amber-100">
                Super Admin Mode
              </span>
              <span className="text-amber-100 hidden xs:inline">Viewing as:</span>
              <span className="font-bold text-white truncate max-w-[180px] sm:max-w-xs md:max-w-md">
                {user?.institute_name || user?.name || 'Institute Dashboard'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleExitImpersonation}
            disabled={exitingImpersonation}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-amber-950 hover:bg-amber-50 active:bg-amber-100 font-bold rounded-lg shadow-xs hover:shadow transition-all shrink-0 text-xs sm:text-sm cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{exitingImpersonation ? 'Returning...' : 'Exit to Super Admin'}</span>
          </button>
        </aside>
      )}

      <div className="flex flex-1 overflow-x-hidden relative md:min-h-0">
        {/* Desktop Sidebar */}
        <aside
        className={`hidden md:flex flex-col min-h-0 overflow-hidden bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 transition-all duration-300 flex-shrink-0 ${collapsed ? 'w-16' : 'w-64'}`}
      >
        {SidebarContent()}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute left-0 top-20 translate-x-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-r-lg p-1 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors z-20"
          style={{ left: collapsed ? '3.5rem' : '15.5rem' }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="w-3 h-3 text-slate-500 dark:text-slate-400" /> : <ChevronLeft className="w-3 h-3 text-slate-500 dark:text-slate-400" />}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-white dark:bg-slate-900 h-full z-50 shadow-xl border-r border-slate-100 dark:border-slate-800">
            {SidebarContent()}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="app-topbar relative z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-100 dark:border-slate-800 px-4 md:px-6 pb-2.5 md:py-4 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Open menu"
              className={`${bottomNavItems.length ? 'hidden' : 'md:hidden'} p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="min-w-0 truncate font-outfit font-bold text-lg sm:text-xl text-slate-900 dark:text-white" title={typeof title === 'string' ? title : undefined}>{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Institute Switcher */}
            {userInstitutes.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowSwitcher(!showSwitcher)}
                  data-testid="institute-switcher-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors max-w-[200px]"
                >
                  <Building2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <span className="truncate hidden sm:inline">
                    {userInstitutes.find(i => i.id === user?.institute_id)?.name || 'Switch'}
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
                {showSwitcher && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSwitcher(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 py-2" data-testid="institute-switcher-dropdown">
                      <p className="px-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Switch Institute</p>
                      {userInstitutes.map(inst => (
                        <button
                          key={inst.id}
                          onClick={() => handleSwitchInstitute(inst.id)}
                          disabled={switching}
                          data-testid={`switch-inst-${inst.id}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${inst.id === user?.institute_id ? 'bg-indigo-50 dark:bg-slate-800' : ''}`}
                        >
                          <div className="w-7 h-7 bg-indigo-100 dark:bg-indigo-950/60 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{inst.name}</p>
                            <p className="text-[10px] text-slate-400">{inst.city || ''}</p>
                          </div>
                          {inst.id === user?.institute_id && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">Active</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Active Session Pill - Click directly redirects to Academic Setup */}
            {activeSession && ['institute_admin', 'teacher', 'student', 'parent'].includes(user?.role) && (
              <button
                type="button"
                onClick={() => ['institute_admin', 'teacher'].includes(user?.role) ? navigate('/institute-panel/academic-setup') : null}
                data-testid="header-active-session-pill"
                title={`Active Academic Session: ${activeSession.name}${activeSession.start_date ? ` (${activeSession.start_date} → ${activeSession.end_date})` : ''} — Click to manage in Academic Setup`}
                className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors ${!['institute_admin', 'teacher'].includes(user?.role) ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span className="hidden lg:inline">Session:</span>
                <span className="truncate max-w-[140px]">{activeSession.name}</span>
              </button>
            )}
            {/* Branch Switcher - Only for institute_admin when multiple branches exist */}
            {user?.role === 'institute_admin' && branches.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowBranchSwitcher(!showBranchSwitcher)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors max-w-[200px]"
                >
                  <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate hidden sm:inline">
                    {selectedBranch ? selectedBranch.name : 'Select Branch'}
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
                {showBranchSwitcher && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowBranchSwitcher(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 py-2">
                      <p className="px-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Select Branch</p>
                      {branches.map(branch => (
                        <button
                          key={branch.id}
                          onClick={() => handleSwitchBranch(branch)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${selectedBranch?.id === branch.id ? 'bg-emerald-50 dark:bg-slate-800' : ''}`}
                        >
                          <div className="w-7 h-7 bg-emerald-100 dark:bg-emerald-950/60 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{branch.name}</p>
                            <p className="text-[10px] text-slate-400">{branch.city || branch.branch_code || ''}</p>
                          </div>
                          {selectedBranch?.id === branch.id && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">Active</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Theme Toggle (Light / Dark) */}
            <ThemeToggle showDropdown={true} />

            {/* Hard Refresh Header Button (For Web & Installed PWA) */}
            <button
              type="button"
              onClick={handleHardRefresh}
              disabled={isHardRefreshing}
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer relative"
              title="Check for and install the latest CoachHub update"
              data-testid="header-hard-refresh-btn"
            >
              <RotateCw className={`w-4 h-4 ${isHardRefreshing ? 'animate-spin text-indigo-600 dark:text-indigo-400' : ''}`} />
            </button>

            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors relative" 
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {globalUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white border-2 border-white dark:border-slate-900">
                    {globalUnreadCount > 9 ? '9+' : globalUnreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <NotificationDropdown 
                  onClose={() => setShowNotifications(false)} 
                  onUnreadCountChange={(count) => setGlobalUnreadCount(count)}
                />
              )}
            </div>
            {/* Profile Avatar with Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowProfileDropdown(!showProfileDropdown); }}
                data-testid="profile-avatar-btn"
                className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold ${!user?.avatar && user?.role !== 'institute_admin' ? roleColor : ''} hover:ring-2 hover:ring-offset-2 hover:ring-slate-300 dark:hover:ring-slate-700 transition-all cursor-pointer overflow-hidden`}
                style={!user?.avatar && user?.role === 'institute_admin' ? { backgroundColor: 'var(--brand-primary, #4f46e5)' } : undefined}
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0)?.toUpperCase()
                )}
              </button>
              {showProfileDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileDropdown(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-100 dark:border-slate-800 py-2 z-50 animate-scale-in" data-testid="profile-dropdown" onClick={(e) => e.stopPropagation()}>
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${!user?.avatar && user?.role !== 'institute_admin' ? roleColor : ''} overflow-hidden flex-shrink-0`}
                        style={!user?.avatar && user?.role === 'institute_admin' ? { backgroundColor: 'var(--brand-primary, #4f46e5)' } : undefined}
                      >
                        {user?.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          user?.name?.charAt(0)?.toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{user?.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
                        <span
                          className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium text-white ${user?.role !== 'institute_admin' ? roleColor : ''}`}
                          style={user?.role === 'institute_admin' ? { backgroundColor: 'var(--brand-primary, #4f46e5)' } : undefined}
                        >
                          {roleLabel}
                        </span>
                      </div>
                    </div>
                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          // Navigate to appropriate profile page based on role
                          if (user?.role === 'institute_admin') navigate('/institute-panel/profile');
                          else if (user?.role === 'teacher') navigate('/teacher/profile');
                          else if (user?.role === 'student') navigate('/student/profile');
                          else if (user?.role === 'super_admin') navigate('/admin/profile');
                          else if (user?.role === 'parent') navigate('/parent/profile');
                        }}
                        data-testid="profile-menu-item"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          // Navigate to settings based on role
                          if (user?.role === 'institute_admin') navigate('/institute-panel/settings');
                          else if (user?.role === 'super_admin') navigate('/admin/storage');
                        }}
                        data-testid="settings-menu-item"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-slate-400" />
                        Settings
                      </button>
                    </div>
                    {/* Logout */}
                    <div className="border-t border-slate-100 dark:border-slate-800 py-1">
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          setShowLogoutModal(true);
                        }}
                        data-testid="logout-menu-item"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main ref={mainRef} className={`flex-1 overflow-y-auto p-4 pb-[calc(6rem+env(safe-area-inset-bottom))] ${isFullHeightPage ? 'md:px-5 md:py-3' : 'md:p-6 md:pb-6'}`}>
          <ErrorBoundary key={location.pathname}>
            <div key={location.pathname} className="app-page-in">
              {!isImpersonating && <PlanExpiryBanner role={user?.role} />}
              {!isImpersonating && <BillingDueBanner summary={billingSummary} role={user?.role} />}
              {children}
            </div>
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar (Android-style navigation bar) */}
      <nav className="app-tabbar fixed bottom-0 left-0 right-0 md:hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200/80 dark:border-slate-800 z-40 safe-area-bottom shadow-[0_-4px_16px_rgba(15,23,42,0.06)]" data-testid="bottom-nav" aria-label="Main">
        <div className="flex items-stretch justify-around px-1 pt-1.5 pb-1">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isSidebarPathActive(location.pathname, item.path) &&
              !bottomNavItems.some(other => other.path !== item.path && location.pathname === other.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl py-1 active:scale-95 transition-transform"
              >
                <span
                  className="flex h-8 w-14 max-w-full items-center justify-center rounded-full transition-colors duration-200"
                  style={isActive ? { backgroundColor: 'rgba(var(--brand-primary-rgb, 79, 70, 229), 0.14)' } : undefined}
                >
                  <Icon className="h-[22px] w-[22px]" style={{ color: isActive ? 'var(--brand-primary, #4f46e5)' : '#64748b' }} strokeWidth={isActive ? 2.4 : 2} />
                </span>
                <span
                  className={`max-w-full truncate text-[11px] leading-none ${isActive ? 'font-bold' : 'font-medium'}`}
                  style={{ color: isActive ? 'var(--brand-primary, #4f46e5)' : '#64748b' }}
                >{item.label}</span>
              </Link>
            );
          })}
          {/* More opens the full menu */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="bottom-nav-more"
            aria-label="More"
            className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl py-1 active:scale-95 transition-transform"
          >
            <span className="flex h-8 w-14 max-w-full items-center justify-center rounded-full" style={mobileOpen ? { backgroundColor: 'rgba(var(--brand-primary-rgb, 79, 70, 229), 0.14)' } : undefined}>
              <Menu className="h-[22px] w-[22px]" style={{ color: mobileOpen ? 'var(--brand-primary, #4f46e5)' : '#64748b' }} />
            </span>
            <span className="text-[11px] font-medium leading-none" style={{ color: mobileOpen ? 'var(--brand-primary, #4f46e5)' : '#64748b' }}>More</span>
          </button>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogOut className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="font-outfit font-bold text-lg text-slate-900 dark:text-white mb-2">Confirm Logout</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Are you sure you want to logout from your account?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutModal(false); handleLogout(); }}
                data-testid="confirm-logout-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
      <AIChatWidget hideTrigger={location.pathname === '/institute-panel/communication/chat'} />
      </div>
    </div>
  );
}
